// Draft detail. Split-pane: original source on the left, structured items
// on the right. The right pane is editable in B5 — each field becomes an
// <input> / <textarea> on focus, plus per-item and per-section delete
// buttons.
//
// Edit model:
//   - The draft from the server is the canonical "original" state.
//   - `working` holds the in-progress edits as a copy of `draft.sections`.
//   - "Save edits" POSTs the entire `working` array; the backend writes
//     `extracted_items` + bumps `items_count`, and the first edit also
//     snapshots the original to extracted_items_original.
//   - Successful save invalidates the detail query so `draft.sections`
//     refreshes from the server and `working` re-syncs against it.
//
// Dirty tracking is a JSON deep-equal against the original. Cheap given
// our payload sizes (largest real draft is ~80 kB JSON) and avoids any
// per-field "is this dirty?" plumbing.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useBeforeUnload, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminShell } from '@/components/AdminShell';
import { StatusBadge } from '@/components/StatusBadge';
import { Dialog } from '@/components/Dialog';
import {
  approveMenuImport,
  editMenuImport,
  fetchMenuImportSource,
  getMenuImport,
  rejectMenuImport,
  type ExtractedMenuItem,
  type ExtractedMenuSection,
  type ImportSourceObject,
  type MenuImportDetail,
} from '@/api/admin';
import { ApiError } from '@/api/client';
import {
  formatDocKind,
  formatDurationMs,
  formatRelativeTime,
  formatUsd,
} from '@/lib/format';

export function DraftDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? Number.parseInt(idParam, 10) : NaN;

  const detail = useQuery<MenuImportDetail, ApiError>({
    queryKey: ['admin', 'menu-imports', id],
    queryFn: ({ signal }) => getMenuImport(id, signal),
    enabled: Number.isFinite(id) && id > 0,
    retry: false,
  });

  if (!Number.isFinite(id) || id <= 0) {
    return (
      <AdminShell>
        <NotFound message="That URL doesn't look like a valid draft id." />
      </AdminShell>
    );
  }

  if (detail.isLoading) {
    return (
      <AdminShell>
        <p className="text-slate-500">Loading draft…</p>
      </AdminShell>
    );
  }

  if (detail.isError) {
    if (detail.error.status === 404) {
      return (
        <AdminShell>
          <NotFound message={`No draft with id ${id}.`} />
        </AdminShell>
      );
    }
    return (
      <AdminShell>
        <ErrorView error={detail.error} onRetry={() => void detail.refetch()} />
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <DraftDetailContent draft={detail.data!} />
    </AdminShell>
  );
}

// ---------------------------------------------------------------------------

function DraftDetailContent({ draft }: { draft: MenuImportDetail }) {
  const queryClient = useQueryClient();
  const [working, setWorking] = useState<ExtractedMenuSection[]>(draft.sections);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Re-sync `working` whenever the server-side draft.sections changes
  // (after a successful save, or if the user navigated away and back).
  // useRef + JSON.stringify avoids resetting in-progress edits on every
  // render when the parent re-renders for unrelated reasons.
  const lastSyncedRef = useRef<string>(JSON.stringify(draft.sections));
  useEffect(() => {
    const incoming = JSON.stringify(draft.sections);
    if (incoming !== lastSyncedRef.current) {
      setWorking(draft.sections);
      lastSyncedRef.current = incoming;
    }
  }, [draft.sections]);

  const isDirty = useMemo(
    () => JSON.stringify(working) !== JSON.stringify(draft.sections),
    [working, draft.sections],
  );

  // Browser-native "unsaved changes" prompt covers tab-close / nav-away.
  // Drafts are only editable when status === 'draft' anyway, so the
  // unsaved-changes window is bounded.
  useBeforeUnload(
    (event) => {
      if (!isDirty) return;
      event.preventDefault();
      // Most browsers ignore custom strings; setting returnValue is the
      // documented way to trigger the prompt.
      event.returnValue = '';
    },
    { capture: true },
  );

  const save = useMutation({
    mutationFn: () => editMenuImport(draft.id, { sections: working }),
    onSuccess: async () => {
      setSaveError(null);
      // Refetch the detail so working re-syncs and items_count updates.
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'menu-imports', draft.id],
      });
      // Also invalidate the list — items_count + extracted_at chips on
      // the table row depend on the edit.
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'menu-imports'],
        exact: false,
      });
    },
    onError: (err: ApiError | Error) => {
      const msg = err instanceof Error ? err.message : String(err);
      setSaveError(msg);
    },
  });

  function revertEdits() {
    setWorking(draft.sections);
    setSaveError(null);
  }

  const editable = draft.status === 'draft';

  return (
    <div>
      <BackLink />

      <header className="mt-3 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {draft.restaurantName}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            <span className="font-semibold tabular-nums">Import #{draft.id}</span>
            <span className="mx-2">·</span>
            <span className="font-mono">{draft.restaurantSlug}</span>
            <span className="mx-2">·</span>
            {formatDocKind(draft.sourceDocKind)}
            <span className="mx-2">·</span>
            extracted {formatRelativeTime(draft.extractedAt)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={draft.status} />
          {editable && (
            <DraftActions draft={draft} working={working} isDirty={isDirty} />
          )}
        </div>
      </header>

      <TelemetryStrip draft={draft} />

      {draft.errorMessage && (
        <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          <strong>Error:</strong> {draft.errorMessage}
        </div>
      )}

      {!editable && (
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          This draft is {draft.status}. Editing is only available for drafts.
        </div>
      )}

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <section className="md:sticky md:top-6 md:self-start md:max-h-[calc(100vh-6rem)]">
          <SectionHeader>Original source</SectionHeader>
          <SourcePreview draft={draft} />
        </section>

        <section>
          <SectionHeader>
            Extracted items
            <span className="ml-2 font-normal text-slate-400">
              {working.length} section{working.length === 1 ? '' : 's'}
              · {working.reduce((s, sec) => s + sec.items.length, 0)} items
            </span>
          </SectionHeader>

          {editable && (
            <EditToolbar
              isDirty={isDirty}
              isSaving={save.isPending}
              onSave={() => save.mutate()}
              onRevert={revertEdits}
              saveError={saveError}
            />
          )}

          <ExtractedSections
            sections={working}
            editable={editable}
            onSectionsChange={setWorking}
          />
        </section>
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/drafts"
      className="text-sm text-slate-500 hover:text-slate-900 underline underline-offset-2"
    >
      ← Drafts
    </Link>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
      {children}
    </h2>
  );
}

// ---------------------------------------------------------------------------

function TelemetryStrip({ draft }: { draft: MenuImportDetail }) {
  return (
    <div className="mt-4 grid gap-x-6 gap-y-2 text-xs text-slate-600 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
      <Telemetry label="Pipeline" value={draft.pipelineVersion} mono />
      <Telemetry label="Extractor" value={draft.extractorUsed} mono />
      <Telemetry label="Structurer" value={draft.structurerUsed} mono />
      <Telemetry label="Cost" value={formatUsd(draft.totalCostUsd)} />
      <Telemetry label="Took" value={formatDurationMs(draft.totalDurationMs)} />
      <Telemetry label="Items" value={String(draft.itemsCount)} />
      <Telemetry
        label="Catalogue reuse"
        value={draft.reuseTelemetry?.reuseRatio === null || !draft.reuseTelemetry
          ? '-'
          : `${Math.round(draft.reuseTelemetry.reuseRatio * 100)}% (${draft.reuseTelemetry.exact} exact, ${draft.reuseTelemetry.aliased} aliased, ${draft.reuseTelemetry.created} created)`}
      />
      <Telemetry
        label="Attribute reuse"
        value={draft.reuseTelemetry
          ? `${draft.reuseTelemetry.attributeAware} promoted · ${draft.reuseTelemetry.attributeConflicts} blocked`
          : '-'}
      />
    </div>
  );
}

function Telemetry({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="font-medium uppercase tracking-wider text-slate-400">{label}</p>
      <p
        className={
          'mt-0.5 text-slate-800 ' + (mono ? 'font-mono text-[11px]' : '')
        }
        title={value}
      >
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function DraftActions({
  draft,
  working,
  isDirty,
}: {
  draft: MenuImportDetail;
  working: ExtractedMenuSection[];
  isDirty: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: () => approveMenuImport(draft.id),
    onSuccess: async () => {
      setActionError(null);
      setShowApprove(false);
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'menu-imports'],
        exact: false,
      });
      // Land back on the drafts list — the row's now `approved` so it
      // disappears from the default `?status=draft` filter, which is the
      // natural "what's left to do?" view.
      navigate('/drafts');
    },
    onError: (err: ApiError | Error) => {
      setActionError(err instanceof Error ? err.message : String(err));
    },
  });

  const reject = useMutation({
    mutationFn: () =>
      rejectMenuImport(draft.id, {
        reason: rejectReason.trim() || undefined,
      }),
    onSuccess: async () => {
      setActionError(null);
      setShowReject(false);
      setRejectReason('');
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'menu-imports'],
        exact: false,
      });
      navigate('/drafts');
    },
    onError: (err: ApiError | Error) => {
      setActionError(err instanceof Error ? err.message : String(err));
    },
  });

  const totalItems = working.reduce((s, sec) => s + sec.items.length, 0);
  const totalSections = working.length;

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-outline text-xs"
          onClick={() => setShowReject(true)}
        >
          Reject
        </button>
        <button
          type="button"
          className="btn-primary text-xs"
          onClick={() => setShowApprove(true)}
          disabled={isDirty}
          title={
            isDirty
              ? 'Save or revert your edits before approving.'
              : undefined
          }
        >
          Approve
        </button>
      </div>

      <Dialog open={showApprove} onClose={() => setShowApprove(false)} widthClass="max-w-lg">
        <h2 className="text-lg font-semibold tracking-tight">Approve this draft?</h2>
        <p className="mt-3 text-sm text-slate-600">
          <strong>{totalItems}</strong> item{totalItems === 1 ? '' : 's'} across{' '}
          <strong>{totalSections}</strong> section{totalSections === 1 ? '' : 's'}{' '}
          will become visible at <strong>{draft.restaurantName}</strong>. This
          replaces the existing approved menu for the matching document kind
          ({formatDocKind(draft.sourceDocKind)}).
        </p>
        {actionError && (
          <p className="mt-3 text-sm text-rose-900">{actionError}</p>
        )}
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setShowApprove(false)}
            disabled={approve.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => approve.mutate()}
            disabled={approve.isPending}
          >
            {approve.isPending ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </Dialog>

      <Dialog open={showReject} onClose={() => setShowReject(false)} widthClass="max-w-lg">
        <h2 className="text-lg font-semibold tracking-tight">Reject this draft?</h2>
        <p className="mt-3 text-sm text-slate-600">
          The draft will be marked rejected. The extracted items stay in the DB
          for audit but won't be visible to users. Optional reason text helps
          future-you remember why.
        </p>
        <label className="mt-4 block">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-500">
            Reason (optional)
          </span>
          <textarea
            className="input mt-1 w-full min-h-[4rem] py-2"
            placeholder="e.g. price column garbled across pages 4-6"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value.slice(0, 500))}
          />
          <span className="text-xs text-slate-400">{rejectReason.length}/500</span>
        </label>
        {actionError && (
          <p className="mt-3 text-sm text-rose-900">{actionError}</p>
        )}
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setShowReject(false)}
            disabled={reject.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => reject.mutate()}
            disabled={reject.isPending}
          >
            {reject.isPending ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </Dialog>
    </>
  );
}

function EditToolbar({
  isDirty,
  isSaving,
  onSave,
  onRevert,
  saveError,
}: {
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
  onRevert: () => void;
  saveError: string | null;
}) {
  return (
    <div className="mb-3 card p-3 flex items-center gap-3 text-sm">
      <span
        className={
          'inline-flex items-center gap-1.5 ' +
          (isDirty ? 'text-amber-700' : 'text-slate-500')
        }
      >
        <span
          className={
            'h-2 w-2 rounded-full ' +
            (isDirty ? 'bg-amber-500' : 'bg-slate-300')
          }
        />
        {isDirty ? 'Unsaved changes' : 'No changes'}
      </span>
      <div className="flex-1" />
      {saveError && (
        <span className="text-xs text-rose-900" title={saveError}>
          Save failed: {saveError}
        </span>
      )}
      <button
        type="button"
        className="btn-ghost text-xs"
        onClick={onRevert}
        disabled={!isDirty || isSaving}
      >
        Revert
      </button>
      <button
        type="button"
        className="btn-primary text-xs"
        onClick={onSave}
        disabled={!isDirty || isSaving}
      >
        {isSaving ? 'Saving…' : 'Save edits'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SourcePreview({ draft }: { draft: MenuImportDetail }) {
  if (draft.sourceKind === 'http_url') {
    return (
      <div className="card p-6">
        <p className="text-sm text-slate-600">
          External URL — opens in a new tab:
        </p>
        <p className="mt-2 break-all font-mono text-xs text-slate-700">
          {draft.sourcePathOrUrl}
        </p>
        <a
          className="btn-outline mt-4 inline-flex"
          href={draft.sourcePathOrUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open ↗
        </a>
      </div>
    );
  }

  return <LocalSourcePreview id={draft.id} sourcePath={draft.sourcePathOrUrl} />;
}

function LocalSourcePreview({ id, sourcePath }: { id: number; sourcePath: string }) {
  const [source, setSource] = useState<ImportSourceObject | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let toRevoke: ImportSourceObject | null = null;
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await fetchMenuImportSource(id, controller.signal);
        if (cancelled) {
          result.revoke();
          return;
        }
        toRevoke = result;
        setSource(result);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      toRevoke?.revoke();
    };
  }, [id]);

  if (error) {
    return (
      <div className="card p-6 text-sm text-rose-900 bg-rose-50 border-rose-200">
        Couldn't load source: <code className="font-mono text-xs">{error}</code>
        <p className="mt-2 text-xs text-slate-600 font-mono break-all">
          {sourcePath}
        </p>
      </div>
    );
  }

  if (!source) {
    return (
      <div className="card p-6 text-sm text-slate-500">Loading source…</div>
    );
  }

  const isPdf = source.contentType.startsWith('application/pdf');
  const isImage = source.contentType.startsWith('image/');

  if (isPdf) {
    return (
      <iframe
        title="Source PDF"
        src={source.url}
        className="card h-[calc(100vh-12rem)] w-full bg-white"
      />
    );
  }
  if (isImage) {
    return (
      <div className="card p-2 bg-slate-50">
        <img src={source.url} alt="Source" className="w-full rounded" />
      </div>
    );
  }
  return (
    <div className="card p-6 text-sm">
      <p className="text-slate-600">
        Can't preview this content type:{' '}
        <code className="font-mono text-xs">{source.contentType}</code>
      </p>
      <a className="btn-outline mt-4 inline-flex" href={source.url} download>
        Download
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editable sections + items
// ---------------------------------------------------------------------------

function ExtractedSections({
  sections,
  editable,
  onSectionsChange,
}: {
  sections: ExtractedMenuSection[];
  editable: boolean;
  onSectionsChange: (next: ExtractedMenuSection[]) => void;
}) {
  if (sections.length === 0) {
    return (
      <div className="card p-6 text-sm text-slate-500">
        No sections to show.
      </div>
    );
  }

  function updateSection(index: number, next: ExtractedMenuSection) {
    const copy = sections.slice();
    copy[index] = next;
    onSectionsChange(copy);
  }

  function deleteSection(index: number) {
    if (!window.confirm('Delete this section and all its items?')) return;
    onSectionsChange(sections.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-6">
      {sections.map((section, i) => (
        <ExtractedSectionView
          key={i}
          section={section}
          index={i}
          editable={editable}
          onChange={(next) => updateSection(i, next)}
          onDelete={() => deleteSection(i)}
        />
      ))}
    </div>
  );
}

function ExtractedSectionView({
  section,
  index,
  editable,
  onChange,
  onDelete,
}: {
  section: ExtractedMenuSection;
  index: number;
  editable: boolean;
  onChange: (next: ExtractedMenuSection) => void;
  onDelete: () => void;
}) {
  function updateItem(itemIndex: number, next: ExtractedMenuItem) {
    const items = section.items.slice();
    items[itemIndex] = next;
    onChange({ ...section, items });
  }
  function deleteItem(itemIndex: number) {
    if (!window.confirm(`Delete "${section.items[itemIndex]?.title}"?`)) return;
    const items = section.items.filter((_, i) => i !== itemIndex);
    onChange({ ...section, items });
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        {editable ? (
          <input
            type="text"
            className="input flex-1 font-semibold"
            value={section.title ?? ''}
            placeholder={`Untitled section #${index + 1}`}
            onChange={(e) =>
              onChange({ ...section, title: e.target.value || undefined })
            }
          />
        ) : (
          <h3 className="flex-1 text-sm font-semibold text-slate-700">
            {section.title ?? (
              <span className="italic text-slate-400">
                Untitled section #{index + 1}
              </span>
            )}
          </h3>
        )}
        <span className="text-xs text-slate-400 whitespace-nowrap">
          {section.items.length} item{section.items.length === 1 ? '' : 's'}
        </span>
        {editable && (
          <button
            type="button"
            className="btn-ghost text-xs text-rose-700 hover:bg-rose-50"
            onClick={onDelete}
          >
            Delete section
          </button>
        )}
      </div>

      <div className="space-y-2">
        {section.items.map((item, j) => (
          <ItemCard
            key={j}
            item={item}
            editable={editable}
            onChange={(next) => updateItem(j, next)}
            onDelete={() => deleteItem(j)}
          />
        ))}
        {section.items.length === 0 && (
          <p className="text-xs italic text-slate-400">No items in this section.</p>
        )}
      </div>
    </div>
  );
}

function ItemCard({
  item,
  editable,
  onChange,
  onDelete,
}: {
  item: ExtractedMenuItem;
  editable: boolean;
  onChange: (next: ExtractedMenuItem) => void;
  onDelete: () => void;
}) {
  if (!editable) {
    return (
      <div className="card p-4">
        <div className="flex items-baseline justify-between gap-3">
          <h4 className="font-medium text-slate-900">{item.title}</h4>
          {item.portionGrams != null && (
            <span className="text-xs tabular-nums text-slate-500 whitespace-nowrap">
              {item.portionGrams} g
            </span>
          )}
        </div>
        {item.description && (
          <p className="mt-1 text-sm text-slate-600">{item.description}</p>
        )}
        {item.ingredients.length > 0 && (
          <p className="mt-2 text-sm text-slate-700">
            <span className="text-xs uppercase tracking-wider text-slate-400 mr-2">
              Ingredients
            </span>
            {item.ingredients.join(', ')}
          </p>
        )}
        {item.allergens.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {item.allergens.map((a) => (
              <span
                key={a}
                className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-mono text-amber-900 ring-1 ring-inset ring-amber-200"
              >
                {a}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Editable form. Ingredients + allergens are comma-separated strings the
  // user can edit freely; we trim + filter empties on the way out so a
  // trailing comma doesn't become a phantom empty ingredient.
  return (
    <div className="card p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-2">
          <FieldRow>
            <Label>Title</Label>
            <input
              type="text"
              className="input"
              value={item.title}
              onChange={(e) => onChange({ ...item, title: e.target.value })}
            />
          </FieldRow>
          <FieldRow>
            <Label>Description</Label>
            <textarea
              className="input min-h-[2.5rem] py-1.5"
              value={item.description ?? ''}
              onChange={(e) =>
                onChange({ ...item, description: e.target.value || undefined })
              }
            />
          </FieldRow>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
            <FieldRow>
              <Label>Ingredients (comma-separated)</Label>
              <input
                type="text"
                className="input font-mono text-xs"
                value={item.ingredients.join(', ')}
                onChange={(e) =>
                  onChange({
                    ...item,
                    ingredients: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Portion (g)</Label>
              <input
                type="number"
                className="input w-24 tabular-nums"
                min={0}
                step={1}
                value={item.portionGrams ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({
                    ...item,
                    portionGrams: v === '' ? undefined : Number(v),
                  });
                }}
              />
            </FieldRow>
          </div>
          <FieldRow>
            <Label>Allergens (comma-separated)</Label>
            <input
              type="text"
              className="input font-mono text-xs"
              value={item.allergens.join(', ')}
              placeholder="e.g. en:gluten, en:milk"
              onChange={(e) =>
                onChange({
                  ...item,
                  allergens: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </FieldRow>
        </div>
        <button
          type="button"
          className="btn-ghost text-xs text-rose-700 hover:bg-rose-50 whitespace-nowrap"
          onClick={onDelete}
          title="Delete item"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-1">{children}</div>;
}
function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-medium uppercase tracking-wider text-slate-400">
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------

function NotFound({ message }: { message: string }) {
  return (
    <div>
      <BackLink />
      <h1 className="mt-3 text-2xl font-semibold tracking-tight">Not found</h1>
      <p className="mt-2 text-slate-600">{message}</p>
    </div>
  );
}

function ErrorView({
  error,
  onRetry,
}: {
  error: ApiError;
  onRetry: () => void;
}) {
  return (
    <div>
      <BackLink />
      <h1 className="mt-3 text-2xl font-semibold tracking-tight">
        Something went wrong
      </h1>
      <p className="mt-2 text-slate-600">
        <code className="font-mono text-sm">{error.message}</code>
      </p>
      <button type="button" className="btn-outline mt-4" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}
